import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useId } from 'react';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { BULK_APPLY_SCOPE } from '../utils/bulkApplyPlan';
import { formatQty } from '../utils/bulkLotNdcStatus';
import { warningTextTone, warningTint } from '../utils/warningTone';

const SCOPE_OPTIONS = [
  { scope: BULK_APPLY_SCOPE.NEEDS_ATTENTION, label: 'Needs attention' },
  { scope: BULK_APPLY_SCOPE.ALL_ROWS, label: 'All rows' },
];

// Rx · facility · quantity · what happens to the row.
const ROW_COLUMNS = '96px 140px 60px minmax(0, 1fr)';

const TONE = {
  change: {
    border: 'divider',
    header: (theme) => alpha(theme.palette.success.main, 0.06),
    title: 'success.dark',
  },
  deficit: {
    border: (theme) => warningTint(theme, 0.5),
    header: (theme) => warningTint(theme, 0.1),
    title: warningTextTone,
  },
  neutral: { border: 'divider', header: 'grey.50', title: 'grey.800' },
};

const formatRowCount = (count) => `${count} row${count === 1 ? '' : 's'}`;

/** Compact pill radios, the picked one ringed, so the choice sits beside the heading. */
const ScopePills = ({ scope, onChange }) => (
  <RadioGroup
    row
    aria-label='Which rows'
    value={scope}
    onChange={(event) => onChange(event.target.value)}
    sx={{ gap: 0.75 }}
  >
    {SCOPE_OPTIONS.map((option) => {
      const isSelected = option.scope === scope;

      return (
        <FormControlLabel
          key={option.scope}
          value={option.scope}
          control={<Radio size='small' sx={{ p: 0.25 }} />}
          label={option.label}
          sx={{
            m: 0,
            pl: 0.5,
            pr: 1.25,
            py: 0.25,
            gap: 0.5,
            border: '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            borderRadius: 4,
            // The second pixel of the ring is a shadow, so picking an option does not resize it.
            boxShadow: (theme) =>
              isSelected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
            bgcolor: (theme) => (isSelected ? alpha(theme.palette.primary.main, 0.06) : undefined),
            '& .MuiFormControlLabel-label': { typography: 'body2' },
          }}
        />
      );
    })}
  </RadioGroup>
);

ScopePills.propTypes = {
  scope: PropTypes.oneOf(Object.values(BULK_APPLY_SCOPE)).isRequired,
  onChange: PropTypes.func.isRequired,
};

const CountChip = ({ tone, children }) => (
  <Box
    component='span'
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: 4,
      typography: 'body2',
      fontWeight: 500,
      bgcolor: TONE[tone].header,
      color: TONE[tone].title,
      border: '1px solid',
      borderColor: TONE[tone].border,
    }}
  >
    {children}
  </Box>
);

CountChip.propTypes = {
  tone: PropTypes.oneOf(Object.keys(TONE)).isRequired,
  children: PropTypes.node.isRequired,
};

/** "Lot4 (Expired lot) → LT55021": the row's lot now, and why it needs attention. */
const LotMove = ({ line, lotNumber }) => (
  <>
    {line.currentLot}
    {line.statusLabel && (
      <Box component='span' sx={{ color: 'error.main', typography: 'caption' }}>
        {` (${line.statusLabel})`}
      </Box>
    )}
    {' → '}
    <Box component='strong' sx={{ color: 'success.dark' }}>
      {lotNumber}
    </Box>
  </>
);

const LINE_SHAPE = PropTypes.shape({
  requestLineItemId: PropTypes.string.isRequired,
  rxNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityName: PropTypes.string,
  fillQuantity: PropTypes.number,
  currentLot: PropTypes.string,
  statusLabel: PropTypes.string,
  deficitQty: PropTypes.number,
  fromStockQty: PropTypes.number,
  reason: PropTypes.string,
});

LotMove.propTypes = { line: LINE_SHAPE.isRequired, lotNumber: PropTypes.string.isRequired };

const OutcomeGroup = ({ title, tone, lines, describe }) => {
  const headingId = useId();
  if (lines.length === 0) return null;
  const isMuted = tone === 'neutral';

  return (
    <Box
      role='group'
      aria-labelledby={headingId}
      sx={{
        border: '1px solid',
        borderColor: TONE[tone].border,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Typography
        id={headingId}
        variant='body2'
        component='h4'
        sx={{
          px: 1.5,
          py: 0.75,
          fontWeight: 700,
          bgcolor: TONE[tone].header,
          color: TONE[tone].title,
          borderBottom: '1px solid',
          borderColor: TONE[tone].border,
        }}
      >
        {`${title} (${lines.length})`}
      </Typography>
      {lines.map((line) => (
        <Box
          key={line.requestLineItemId}
          sx={{
            display: 'grid',
            gridTemplateColumns: ROW_COLUMNS,
            columnGap: 1.25,
            alignItems: 'center',
            px: 1.5,
            py: 1,
            typography: 'body2',
            color: isMuted ? 'text.secondary' : 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 0 },
          }}
        >
          <Box component='span' sx={{ fontFamily: 'monospace' }}>
            {line.rxNumber}
          </Box>
          <Box component='span' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {line.facilityName}
          </Box>
          <span>{`Qty ${formatQty(line.fillQuantity)}`}</span>
          <Box component='span' sx={{ minWidth: 0 }}>
            {describe(line)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

OutcomeGroup.propTypes = {
  title: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(Object.keys(TONE)).isRequired,
  lines: PropTypes.arrayOf(LINE_SHAPE).isRequired,
  describe: PropTypes.func.isRequired,
};

/** "60 from stock, 30 as System Deficit", or all of it as System Deficit. */
const describeDeficit = (line) =>
  line.fromStockQty > 0
    ? `${formatQty(line.fromStockQty)} from stock, ${formatQty(line.deficitQty)} as ${SYSTEM_DEFICIT}`
    : `all ${formatQty(line.deficitQty)} as ${SYSTEM_DEFICIT}`;

/**
 * POM-6537 — "What will happen": what Apply would do to every row of the picked NDC, before it
 * does it (describeBulkLotUpdate). There is no tick per row: what is listed here is what Apply
 * does, a row that should stay is undone under Pending commit afterwards, and the whole Apply with
 * "Undo bulk change". Which rows is asked only when the answers differ (`hasScopeChoice`).
 */
const BulkLotUpdatePreview = ({ outcome, lot, scope, hasScopeChoice, onScopeChange }) => {
  const { lotNumber, isManual } = lot;
  const describeChange = (line) => <LotMove line={line} lotNumber={lotNumber} />;

  return (
    <Stack spacing={1.25}>
      <Stack direction='row' alignItems='center' sx={{ gap: 1.5, flexWrap: 'wrap', minHeight: 32 }}>
        <Typography
          variant='subtitle1'
          component='h3'
          sx={{ fontWeight: 700, lineHeight: 1.3, flexGrow: 1 }}
        >
          What will happen
        </Typography>
        {hasScopeChoice && <ScopePills scope={scope} onChange={onScopeChange} />}
      </Stack>

      <Stack direction='row' sx={{ gap: 1, flexWrap: 'wrap' }}>
        {outcome.changeCount > 0 ? (
          <CountChip tone='change'>{`${formatRowCount(outcome.changeCount)} will change`}</CountChip>
        ) : (
          <CountChip tone='neutral'>{`No row can take lot ${lotNumber}`}</CountChip>
        )}
        {outcome.deficitQty > 0 && (
          <CountChip tone='deficit'>
            {`${formatQty(outcome.deficitQty)} as ${SYSTEM_DEFICIT}`}
          </CountChip>
        )}
        {outcome.alreadyOn.length > 0 && (
          <CountChip tone='neutral'>{`${outcome.alreadyOn.length} already on ${lotNumber}`}</CountChip>
        )}
        {outcome.skipped.length > 0 && (
          <CountChip tone='neutral'>{`${outcome.skipped.length} skipped`}</CountChip>
        )}
      </Stack>

      <OutcomeGroup
        title={`Will change to ${lotNumber}`}
        tone='change'
        lines={outcome.change}
        describe={describeChange}
      />
      <OutcomeGroup
        title={
          isManual
            ? `Will change, recorded by hand: all as ${SYSTEM_DEFICIT}`
            : `Will change, part as ${SYSTEM_DEFICIT}`
        }
        tone='deficit'
        lines={outcome.changeWithDeficit}
        describe={(line) => (
          <>
            {describeChange(line)}
            {' · '}
            <Box component='span' sx={{ color: warningTextTone }}>
              {isManual
                ? `${formatQty(line.deficitQty)} as ${SYSTEM_DEFICIT}`
                : describeDeficit(line)}
            </Box>
          </>
        )}
      />
      <OutcomeGroup
        title={`Already on ${lotNumber}`}
        tone='neutral'
        lines={outcome.alreadyOn}
        describe={() => 'Nothing to change'}
      />
      <OutcomeGroup
        title='Skipped'
        tone='neutral'
        lines={outcome.skipped}
        describe={(line) => line.reason}
      />
    </Stack>
  );
};

BulkLotUpdatePreview.propTypes = {
  /** describeBulkLotUpdate's output. */
  outcome: PropTypes.shape({
    change: PropTypes.arrayOf(LINE_SHAPE).isRequired,
    changeWithDeficit: PropTypes.arrayOf(LINE_SHAPE).isRequired,
    alreadyOn: PropTypes.arrayOf(LINE_SHAPE).isRequired,
    skipped: PropTypes.arrayOf(LINE_SHAPE).isRequired,
    changeCount: PropTypes.number.isRequired,
    deficitQty: PropTypes.number.isRequired,
  }).isRequired,
  /** The picked lot: from stock, or recorded by hand. */
  lot: PropTypes.shape({ lotNumber: PropTypes.string.isRequired, isManual: PropTypes.bool })
    .isRequired,
  scope: PropTypes.oneOf(Object.values(BULK_APPLY_SCOPE)).isRequired,
  hasScopeChoice: PropTypes.bool.isRequired,
  onScopeChange: PropTypes.func.isRequired,
};

export default BulkLotUpdatePreview;
